<template>
  <div
    class="kk-select"
    :class="[
      expanded && 'kk-select--expanded',
      isDisabled && 'kk-select--disabled',
      !isDisabled && hasError && 'kk-select--error',
      leadingImage && 'kk-select--leading',
      trailingIcon && 'kk-select--trailing',
      $slots['default'] && 'kk-select--auto-height',
    ]"
    :aria-expanded="String(expanded)"
    @click="onClickElement"
  >
    <slot name="leading-image">
      <KkSelectLeadingImage
        v-if="leadingImage"
        class="kk-select__leading-image"
        :config="leadingImage"
        :is-disabled="isDisabled"
      />
    </slot>
    <input
      v-if="isFocus && isFilterable && !isDisabled"
      ref="inputElement"
      v-bind="$attrs"
      :value="query"
      class="kk-select__input"
      type="text"
      :placeholder="currentValue || placeholder"
      v-on="listeners"
      @focus="focusHandler"
      @blur="blurHandler"
      @input="$emit('update:query', $event.target.value)"
    />
    <div
      v-else-if="$slots['default']"
      ref="inputElement"
      class="kk-select__main-content"
      tabindex="0"
    >
      <slot></slot>
    </div>
    <input
      v-else
      ref="inputElement"
      v-model="currentValue"
      v-bind="$attrs"
      class="kk-select__input"
      type="text"
      readonly
      :placeholder="placeholder"
      :disabled="isDisabled"
      v-on="listeners"
      @focus="focusHandler"
      @blur="blurHandler"
    />
    <KkIcon
      v-if="trailingIcon"
      class="kk-select__trailing-icon"
      :class="{
        'kk-select__trailing-icon--transition': hasTrailingTransition,
      }"
      :name="trailingIcon"
      size="sm"
    />
  </div>
</template>

<script>
import { isVue2 } from 'vue-demi';
import KkIcon from '../../KkIcon';
import KkSelectLeadingImage from '../../KkSelectLeadingImage';

export const KkSelectProps = {
  leadingImage: {
    type: [String, Object],
    default: '',
  },
  trailingIcon: {
    type: String,
    default: 'ic_arrowDown_line',
  },
  hasTrailingTransition: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  value: {
    type: [String, Number],
    default: '',
  },
  query: {
    type: [String, Number],
    default: '',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isFilterable: {
    type: Boolean,
    default: false,
  },
  isExpanded: {
    type: Boolean,
    required: false,
  },
};
export default {
  name: 'KkSelect',
  components: {
    KkSelectLeadingImage,
    KkIcon,
  },
  props: KkSelectProps,
  data() {
    return {
      expanded: false,
      isFocus: false,
    };
  },
  computed: {
    currentValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    listeners() {
      if (isVue2) {
        // eslint-disable-next-line no-unused-vars
        const { input, focus, blur, ...restListeners } = this.$listeners;
        return restListeners;
      }

      // eslint-disable-next-line no-unused-vars
      const { input, focus, blur, ...restListeners } = this.$attrs;
      return restListeners;
    },
  },
  watch: {
    isExpanded: {
      immediate: true,
      handler(val) {
        this.expanded = val;
      },
    },
  },
  methods: {
    focusHandler($event) {
      this.$emit('focus', $event);
    },
    blurHandler($event) {
      this.$emit('blur', $event);
      this.setSelectFocus(false);
    },
    setSelectFocus(isFocus) {
      this.isFocus = isFocus;
    },
    onClickElement() {
      if (this.isDisabled) {
        return;
      }
      if (!this.hasProp('isExpanded')) {
        this.expanded = !this.expanded;
      } else {
        this.$emit('update:isExpanded', !this.expanded);
      }
      this.setSelectFocus(true);
      this.$nextTick(() => {
        this.$refs.inputElement.focus();
      });
    },
    hasProp(prop) {
      const $options = this.$options || {};
      const propsData = $options.propsData || {};
      return prop in propsData;
    },
  },
};
</script>
